import React from 'react';
import { useSystemCheck } from '../hooks/useSystemCheck';
import { CameraPreview } from './CameraPreview';
import { MicrophoneMeter } from './MicrophoneMeter';
import { SpeakerTest } from './SpeakerTest';
import { SystemCheckList } from './SystemCheckList';
import { ReadinessSummary } from './ReadinessSummary';

export interface SystemCheckModalProps {
  isOpen: boolean;
  scenarioTitle?: string;
  requiresMicrophone?: boolean;
  requiresCamera?: boolean;
  requiresSpeaker?: boolean;
  onComplete: () => void;
  onCancel: () => void;
}

export const SystemCheckModal: React.FC<SystemCheckModalProps> = ({
  isOpen,
  scenarioTitle,
  requiresMicrophone = true,
  requiresCamera = true,
  requiresSpeaker = false,
  onComplete,
  onCancel,
}) => {
  const {
    checks,
    readiness,
    isReady,
    isCheckingAll,
    cameraStream,
    audioLevel,
    speakerConfirmed,
    isPlayingTone,
    runAllChecks,
    retryCheck,
    playTestChime,
    confirmSpeaker,
    stopAllStreams,
  } = useSystemCheck({
    requiresMicrophone,
    requiresCamera,
    requiresSpeaker,
    autoStart: isOpen,
  });

  if (!isOpen) return null;

  const handleProceed = () => {
    stopAllStreams();
    onComplete();
  };

  const handleClose = () => {
    stopAllStreams();
    onCancel();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="system-check-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(0,0,0,0.80)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '880px',
          borderRadius: '18px',
          border: '1px solid rgba(255,255,255,0.10)',
          background: 'rgba(15, 18, 30, 0.97)',
          padding: '24px',
          color: '#fff',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            paddingBottom: '16px',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '22px' }}>🚀</span>
              <h2 id="system-check-title" style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
                System Pre-Flight Readiness
              </h2>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94A3B8' }}>
              {scenarioTitle ? (
                <>Preparing device environment for <strong style={{ color: '#CBD5E1' }}>{scenarioTitle}</strong></>
              ) : (
                'Verify your audio, camera, and network connection before entering simulation.'
              )}
            </p>
          </div>

          <ReadinessSummary readiness={readiness} isChecking={isCheckingAll} />
        </div>

        {/* ── Main Content ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
          }}
        >
          {/* Left: Hardware Feeds */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Camera */}
            {requiresCamera ? (
              <div>
                <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#CBD5E1', marginBottom: '6px' }}>
                  📷 Camera Live View
                </span>
                <CameraPreview
                  stream={cameraStream}
                  status={checks.find((c) => c.id === 'camera')?.status}
                />
              </div>
            ) : (
              <div
                style={{
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(0,0,0,0.3)',
                  padding: '16px',
                  textAlign: 'center',
                }}
              >
                <span style={{ fontSize: '24px' }}>🎧</span>
                <p style={{ margin: '6px 0 2px', fontSize: '13px', fontWeight: 600, color: '#E2E8F0' }}>Audio Simulation Mode</p>
                <p style={{ margin: 0, fontSize: '11px', color: '#94A3B8' }}>Camera video is optional and not required for this scenario.</p>
              </div>
            )}

            {/* Microphone meter */}
            <div
              style={{
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(0,0,0,0.4)',
                padding: '12px 14px',
              }}
            >
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#CBD5E1', marginBottom: '8px' }}>
                🎙 Live Microphone Signal
              </span>
              <MicrophoneMeter
                level={audioLevel}
                isActive={checks.find((c) => c.id === 'microphone')?.status === 'passed'}
              />
            </div>

            {/* Speaker test */}
            {requiresSpeaker && (
              <SpeakerTest
                isPlayingTone={isPlayingTone}
                speakerConfirmed={speakerConfirmed}
                onPlayTone={playTestChime}
                onConfirm={confirmSpeaker}
              />
            )}
          </div>

          {/* Right: Diagnostic checklist */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Diagnostic Checkpoints
                </span>
                <button
                  type="button"
                  onClick={runAllChecks}
                  disabled={isCheckingAll}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: isCheckingAll ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: isCheckingAll ? '#475569' : '#818CF8',
                    padding: 0,
                  }}
                >
                  {isCheckingAll ? 'Running Checks...' : '↻ Re-test All'}
                </button>
              </div>
              <SystemCheckList checks={checks} onRetry={retryCheck} />
            </div>

            {/* Privacy note */}
            <div
              style={{
                borderRadius: '10px',
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.06)',
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '14px', marginTop: '1px', userSelect: 'none' }}>🔒</span>
              <p style={{ margin: 0, fontSize: '11px', color: '#64748B', lineHeight: 1.5 }}>
                <strong style={{ color: '#94A3B8' }}>Device Privacy:</strong> Your camera and microphone are accessed
                locally in your browser only to verify readiness. No video or audio is recorded or uploaded during this check.
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            paddingTop: '16px',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: '12px' }}>
            {isReady ? (
              <span style={{ color: '#34D399', fontWeight: 600 }}>✓ All required hardware is verified and ready.</span>
            ) : (
              <span style={{ color: '#FCD34D' }}>⚠ Please resolve blocked requirements before proceeding.</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '8px',
                color: '#CBD5E1',
                fontSize: '13px',
                fontWeight: 600,
                padding: '8px 18px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProceed}
              disabled={!isReady || isCheckingAll}
              style={{
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 700,
                padding: '9px 22px',
                cursor: isReady && !isCheckingAll ? 'pointer' : 'not-allowed',
                border: 'none',
                ...(isReady && !isCheckingAll
                  ? {
                      background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                      color: '#fff',
                      boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
                    }
                  : {
                      background: 'rgba(255,255,255,0.06)',
                      color: '#475569',
                    }),
              }}
            >
              Proceed to Simulation →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
